import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { GoogleMap, Marker, StandaloneSearchBox, useLoadScript } from "@react-google-maps/api";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { updateUserLocationRequest, fetchUserInfo } from "store/actions";
import appwritPointes from "helpers/appwrite/appwritePointes";

const libraries = ["places"];

const LoadingContainer = () => (
  <div style={{
    textAlign: 'center',
    width: '100%',
    height: '15rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontSize: '27px',
    fontWeight: 700,
  }}> 
    <b>🌐</b> جاري تحميل الخريطة
  </div>
);

const MapsGoogle = () => {
  document.title = "Google Maps | DBSaloni - Admin";

  // Memoized selector to prevent unnecessary re-renders
  const user = useSelector(state => state.Login.user);
  const { location_latitude = 0, location_longitude = 0, documentId } = user || {};

  const [storePosition, setStorePosition] = useState({
    lat: parseFloat(location_latitude) || 24.7136, // Default to Riyadh coordinates
    lng: parseFloat(location_longitude) || 46.6753
  });

  const [selectedMarkerPosition, setSelectedMarkerPosition] = useState(storePosition);
  const [isCurrentLocationSelected, setIsCurrentLocationSelected] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [map, setMap] = useState(null);

  const dispatch = useDispatch();
  const searchBoxRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: appwritPointes.googleMapId,
    libraries,
  });

  // Memoize the user info fetching
  useEffect(() => {
    const usersessionId = Cookies.get("userId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });
    if (usersessionId) {
      dispatch(fetchUserInfo(usersessionId));
    }
  }, [dispatch]);

  // Update positions when location data changes
  useEffect(() => {
    if (location_latitude && location_longitude) {
      const newPosition = {
        lat: parseFloat(location_latitude),
        lng: parseFloat(location_longitude),
      };
      setStorePosition(newPosition);
      setSelectedMarkerPosition(newPosition);
    }
  }, [location_latitude, location_longitude]);

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setSelectedMarkerPosition({ lat, lng });
    setIsCurrentLocationSelected(false);
  }, []);

  const handleAddLocation = useCallback(() => {
    if (!isCurrentLocationSelected) {
      dispatch(
        updateUserLocationRequest(
          documentId,
          selectedMarkerPosition.lat,
          selectedMarkerPosition.lng
        )
      );
      toast.success("تم تحديث موقعك بنجاح");
      setStorePosition(selectedMarkerPosition);
      setIsCurrentLocationSelected(true);
    } else {
      toast.error("الرجاء تحديد موقع جديد على الخريطة.");
    }
  }, [dispatch, documentId, isCurrentLocationSelected, selectedMarkerPosition]);

  const handleCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const newPosition = { lat, lng };
          setSelectedMarkerPosition(newPosition);
          setStorePosition(newPosition);
          setIsCurrentLocationSelected(false);
          if (map) {
            map.panTo(newPosition);
          }
        },
        (error) => {
          toast.error(`خطأ: فشل خدمة تحديد الموقع الجغرافي (${error.message})`);
        }
      );
    } else {
      toast.error("لا يدعم متصفحك تحديد الموقع الجغرافي");
    }
  }, [map]);

  const onMapLoad = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    setMap(mapInstance);
  }, []);

  const onSearchBoxLoad = useCallback((ref) => {
    searchBoxRef.current = ref;
  }, []);

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces();
    if (!places || places.length === 0) return;
    
    const place = places[0];
    const location = place.geometry?.location;
    if (!location) return;

    const lat = location.lat();
    const lng = location.lng();
    const newPosition = { lat, lng };
    
    setSelectedMarkerPosition(newPosition);
    setStorePosition(newPosition);
    setIsCurrentLocationSelected(false);
    setSearchInput(place.formatted_address || "");
    
    if (mapRef.current) {
      mapRef.current.panTo(newPosition);
    }
  }, []);

  // Memoize styles to prevent unnecessary re-renders
  const mapContainerStyle = useMemo(() => ({
    height: "50vh",
    width: "100%",
  }), []);

  const style = useMemo(() => ({
    overflow: "hidden",
    height: "59vh",
    paddingBottom: "1rem",
  }), []);

  const buttonStyle = useMemo(() => ({
    fontSize: "1rem",
    fontWeight: "800",
    backgroundColor: '#556ee6',
  }), []);

  const buttDiv = useMemo(() => ({
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
    width: "100%",
    marginBottom: "1rem",
    padding: "1rem",
  }), []);

  const inputStyle = useMemo(() => ({
    boxSizing: 'border-box',
    border: '1px solid #6841ea47',
    width: '100%',
    height: '58px',
    padding: '0px 12px',
    borderRadius: '10px',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 2px 6px',
    fontSize: '14px',
    outline: 'none',
    textOverflow: 'ellipsis',
  }), []);

  if (loadError) {
    return <div>عذرًا ، لا يمكن تحميل الخريطة حاليًا. يرجى تحديث الصفحة ومحاولة مرة أخرى.</div>;
  }

  if (!isLoaded) {
    return <LoadingContainer />;
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody style={style}>
              <StandaloneSearchBox
                onLoad={onSearchBoxLoad}
                onPlacesChanged={onPlacesChanged}
              >
                <input
                  type="text"
                  placeholder="ابحث عن عنوان الفرع"
                  style={inputStyle}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </StandaloneSearchBox>
              <div id="map" style={{ position: "relative", paddingTop: "1rem" }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={14}
                  center={storePosition}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <Marker position={selectedMarkerPosition} />
                </GoogleMap>
              </div>
            </CardBody>
            <div style={buttDiv}>
              <Button
                style={buttonStyle}
                onClick={handleAddLocation}
                disabled={isCurrentLocationSelected}
              >
                إضافة الموقع
              </Button>
              <Button
                style={buttonStyle}
                onClick={handleCurrentLocation}
              >
                الموقع الحالي
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default MapsGoogle;